import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { AlertTriangle } from 'lucide-react';

/**
 * KarpenterNodeClassFormModal — Phase C.5 Create / Edit / Delete NodeClass.
 *
 * Cloud-conditional form. cloud === 'aws' renders EC2NodeClass fields
 * (AMI / IAM / subnets / SGs / block devices / metadata options).
 * cloud === 'gcp' renders GCPNodeClass fields (image family / service
 * account / project / region / network interfaces).
 *
 * Modes:
 *   - "create": full form, POST .../karpenter/nodeclasses
 *   - "edit":   prefilled from `nodeclass`, name disabled, sparse PATCH
 *               diff (only changed fields). PUT .../karpenter/nodeclasses/{name}
 *   - "delete": confirm + force checkbox + type-the-name guard.
 *               DELETE .../karpenter/nodeclasses/{name}?force=…
 *
 * Same operator-approval rail as Phase C — submitting creates a pending
 * action. The existing Recommendations table is the approval surface.
 */

const AMI_FAMILIES = ['AL2', 'AL2023', 'Bottlerocket', 'Ubuntu', 'Custom'];

// ─── Selector-term editor (id / tags / name) ─────────────────────────────
const SelectorTermsEditor = ({ label, value, onChange, disabled, hint }) => {
  const terms = value || [];
  const update = (i, patch) => {
    const next = [...terms];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => onChange(terms.filter((_, j) => j !== i));
  const add = () => onChange([...terms, { tags: {} }]);

  return (
    <div className="border rounded p-2 bg-gray-50">
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-medium text-gray-700">{label}</label>
        <Button size="sm" variant="ghost" onClick={add} disabled={disabled}>+ Add</Button>
      </div>
      {hint && <p className="text-xs text-gray-400 mb-1">{hint}</p>}
      {terms.map((t, i) => (
        <div key={i} className="flex gap-1 mb-1">
          <input
            placeholder="id (e.g. subnet-abc)"
            value={t.id || ''}
            disabled={disabled}
            onChange={(e) => update(i, { id: e.target.value })}
            className="flex-1 text-xs px-2 py-1 border rounded"
          />
          <input
            placeholder='tag-key=value'
            value={
              t.tags
                ? Object.entries(t.tags).map(([k, v]) => `${k}=${v}`).join(',')
                : ''
            }
            disabled={disabled}
            onChange={(e) => {
              const tags = {};
              e.target.value.split(',').forEach((kv) => {
                const [k, v] = kv.split('=');
                if (k && v) tags[k.trim()] = v.trim();
              });
              update(i, { tags });
            }}
            className="flex-1 text-xs px-2 py-1 border rounded"
          />
          <Button size="sm" variant="ghost" onClick={() => remove(i)} disabled={disabled}>✕</Button>
        </div>
      ))}
    </div>
  );
};

// Compute the sparse diff between the current form and the prefilled
// original — only changed fields go into the PATCH body.
const diffPayload = (form, original) => {
  const out = {};
  const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const fields = [
    // EC2NodeClass fields
    'ami_family', 'ami_selector_terms', 'subnet_selector_terms',
    'security_group_selector_terms', 'role', 'instance_profile',
    'tags', 'block_device_mappings', 'metadata_options', 'user_data',
    // GCPNodeClass fields
    'image_family', 'service_account', 'network_interfaces', 'metadata',
  ];
  for (const f of fields) {
    if (!same(form[f], original[f])) out[f] = form[f];
  }
  return out;
};

const KarpenterNodeClassFormModal = ({
  open,
  mode,
  cloud,
  clusterId,
  nodeclass,    // existing nodeclass row (edit/delete)
  onClose,
  onSuccess,
}) => {
  const initial = useMemo(() => {
    if (mode === 'create') {
      return cloud === 'gcp'
        ? {
            name: '',
            image_family: 'Ubuntu2204',
            service_account: '',
            gcp_project: '',
            region: '',
            network_interfaces: [],
            tags: {},
            metadata: {},
          }
        : {
            name: '',
            ami_family: 'AL2023',
            ami_selector_terms: [],
            subnet_selector_terms: [],
            security_group_selector_terms: [],
            role: '',
            instance_profile: '',
            tags: {},
            block_device_mappings: [],
            metadata_options: {},
            user_data: '',
          };
    }
    // edit / delete: prefill from nodeclass
    return nodeclass || {};
  }, [mode, cloud, nodeclass]);

  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [forceDelete, setForceDelete] = useState(false);
  const [iamMode, setIamMode] = useState(
    initial?.instance_profile ? 'instance_profile' : 'role'
  );

  useEffect(() => {
    setForm(initial);
    setError(null);
  }, [initial]);

  const update = (patch) => setForm((s) => ({ ...s, ...patch }));

  const handleSubmit = async () => {
    if (!clusterId) return;
    setSubmitting(true);
    setError(null);
    try {
      let url, method, body, name;
      if (mode === 'create') {
        if (!form.name) { setError('Name is required'); setSubmitting(false); return; }
        if (cloud === 'aws') {
          if (!form.role && !form.instance_profile) {
            setError('Role or InstanceProfile is required');
            setSubmitting(false); return;
          }
          if (form.role && form.instance_profile) {
            setError('Specify exactly one of role / instance_profile');
            setSubmitting(false); return;
          }
          if (!(form.subnet_selector_terms || []).length) {
            setError('At least one subnet selector term is required');
            setSubmitting(false); return;
          }
          if (!(form.security_group_selector_terms || []).length) {
            setError('At least one security group selector term is required');
            setSubmitting(false); return;
          }
        } else {
          if (!form.service_account || !form.gcp_project || !form.region) {
            setError('Service account, project, and region are required');
            setSubmitting(false); return;
          }
        }
        url = `${API}/v1/clusters/${clusterId}/karpenter/nodeclasses`;
        method = 'post';
        body = form;
        name = form.name;
      } else if (mode === 'edit') {
        const patch = diffPayload(form, nodeclass || {});
        if (Object.keys(patch).length === 0) {
          setError('No changes to apply');
          setSubmitting(false); return;
        }
        url = `${API}/v1/clusters/${clusterId}/karpenter/nodeclasses/${encodeURIComponent(nodeclass.name)}`;
        method = 'put';
        body = patch;
        name = nodeclass.name;
      } else if (mode === 'delete') {
        if (forceDelete && confirmName !== nodeclass.name) {
          setError(`Type "${nodeclass.name}" exactly to confirm force-delete`);
          setSubmitting(false); return;
        }
        url = `${API}/v1/clusters/${clusterId}/karpenter/nodeclasses/${encodeURIComponent(nodeclass.name)}?force=${forceDelete}`;
        method = 'delete';
        body = null;
        name = nodeclass.name;
      }
      const res = method === 'delete'
        ? await axios.delete(url)
        : await axios[method](url, body);
      onSuccess?.({ mode, name, action_id: res.data?.action_id });
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' && `Create ${cloud === 'gcp' ? 'GCPNodeClass' : 'EC2NodeClass'}`}
            {mode === 'edit' && `Edit '${nodeclass?.name}'`}
            {mode === 'delete' && `Delete '${nodeclass?.name}'`}
          </DialogTitle>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
            {error}
          </div>
        )}

        {mode === 'delete' ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              This will delete the NodeClass. With <strong>force=false</strong> the
              agent refuses if any NodePool's <code>nodeClassRef</code> still
              points at it. With <strong>force=true</strong> the class is removed
              immediately and any referencing pools become inert until re-pointed.
            </p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={forceDelete}
                onChange={(e) => setForceDelete(e.target.checked)}
              />
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Force delete (allows referencing pools to be left inert)
            </label>
            {forceDelete && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Type the class name to confirm:
                </label>
                <input
                  type="text"
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  className="w-full text-sm px-2 py-1.5 border rounded"
                  placeholder={nodeclass?.name}
                />
              </div>
            )}
          </div>
        ) : cloud === 'gcp' ? (
          // ─── GCPNodeClass form ───
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text" value={form.name || ''}
                  disabled={mode === 'edit'}
                  onChange={(e) => update({ name: e.target.value })}
                  className="w-full text-sm px-2 py-1.5 border rounded disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Image Family</label>
                <input
                  type="text" value={form.image_family || ''}
                  onChange={(e) => update({ image_family: e.target.value })}
                  className="w-full text-sm px-2 py-1.5 border rounded"
                  placeholder="Ubuntu2204"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Service Account</label>
              <input
                type="text" value={form.service_account || ''}
                onChange={(e) => update({ service_account: e.target.value })}
                className="w-full text-sm px-2 py-1.5 border rounded"
                placeholder="karpenter@my-project.iam.gserviceaccount.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Project</label>
                <input
                  type="text" value={form.gcp_project || ''}
                  onChange={(e) => update({ gcp_project: e.target.value })}
                  className="w-full text-sm px-2 py-1.5 border rounded"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Region</label>
                <input
                  type="text" value={form.region || ''}
                  onChange={(e) => update({ region: e.target.value })}
                  className="w-full text-sm px-2 py-1.5 border rounded"
                  placeholder="us-central1"
                />
              </div>
            </div>
          </div>
        ) : (
          // ─── EC2NodeClass form ───
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text" value={form.name || ''}
                  disabled={mode === 'edit'}
                  onChange={(e) => update({ name: e.target.value })}
                  className="w-full text-sm px-2 py-1.5 border rounded disabled:bg-gray-100"
                  placeholder="default"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">AMI Family</label>
                <select
                  value={form.ami_family || 'AL2023'}
                  onChange={(e) => update({ ami_family: e.target.value })}
                  className="w-full text-sm px-2 py-1.5 border rounded"
                >
                  {AMI_FAMILIES.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>

            {/* IAM — exactly one of role / instance_profile */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">IAM</label>
              <div className="flex items-center gap-3 mb-1">
                <label className="flex items-center gap-1 text-sm cursor-pointer">
                  <input
                    type="radio"
                    checked={iamMode === 'role'}
                    onChange={() => {
                      setIamMode('role');
                      update({ instance_profile: '' });
                    }}
                  />
                  Role
                </label>
                <label className="flex items-center gap-1 text-sm cursor-pointer">
                  <input
                    type="radio"
                    checked={iamMode === 'instance_profile'}
                    onChange={() => {
                      setIamMode('instance_profile');
                      update({ role: '' });
                    }}
                  />
                  Instance Profile
                </label>
              </div>
              <input
                type="text"
                value={iamMode === 'role' ? (form.role || '') : (form.instance_profile || '')}
                onChange={(e) =>
                  iamMode === 'role'
                    ? update({ role: e.target.value })
                    : update({ instance_profile: e.target.value })
                }
                className="w-full text-sm px-2 py-1.5 border rounded"
                placeholder={iamMode === 'role' ? 'KarpenterNodeRole' : 'KarpenterNodeInstanceProfile'}
              />
            </div>

            <SelectorTermsEditor
              label="Subnet Selector Terms (required)"
              hint="One or more entries: by id (subnet-abc) or tags (karpenter.sh/discovery=test)"
              value={form.subnet_selector_terms}
              onChange={(v) => update({ subnet_selector_terms: v })}
            />
            <SelectorTermsEditor
              label="Security Group Selector Terms (required)"
              hint="One or more: by id (sg-xyz) or tags"
              value={form.security_group_selector_terms}
              onChange={(v) => update({ security_group_selector_terms: v })}
            />
            <SelectorTermsEditor
              label="AMI Selector Terms (optional)"
              hint="Refines AMI family — by alias (al2023@latest), id, owner, or tags"
              value={form.ami_selector_terms}
              onChange={(v) => update({ ami_selector_terms: v })}
            />

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">User Data (optional)</label>
              <textarea
                value={form.user_data || ''}
                onChange={(e) => update({ user_data: e.target.value })}
                className="w-full text-xs px-2 py-1.5 border rounded font-mono"
                rows={3}
                placeholder="#!/bin/bash&#10;# Custom bootstrap script"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            variant={mode === 'delete' ? 'destructive' : 'default'}
          >
            {submitting ? 'Submitting…'
              : mode === 'create' ? 'Create'
              : mode === 'edit' ? 'Submit Update' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KarpenterNodeClassFormModal;
